import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import { diskPresets } from '@/components/volumes/gcpDiskSettings';
import { volumePresets } from '@/components/volumes/volumeSettings';
import { convertToBytes, fileSizeFormatter } from '@/utils/fileSizeFormatter';

import { KmsKeyRow } from './kmsKeyRow';

export const VolumeSection = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  if (resource.cloudProvider === Provider.Aws) {
    return <AwsVolumeSection resource={resource} />;
  }

  if (resource.cloudProvider === Provider.Gcp) {
    return <GcpDiskSection resource={resource} />;
  }

  return null;
};

const AwsVolumeSection = ({ resource }: { resource: InventoryResource }) => {
  if (resource.resourceProperties?.awsEbsVolume) {
    const ebsVolume = resource.resourceProperties.awsEbsVolume;

    return (
      <Stack className='px-[40px] py-[24px]'>
        <Stack direction={'row'} gap={'60px'}>
          <Table
            sx={{
              tableLayout: 'fixed',
              td: {
                padding: '4px 0',
                borderBottom: 'none',
                lineHeight: '24px',
                height: '24px',
              },

              '& .props-col': {
                whiteSpace: 'nowrap',
                fontWeight: '400',
                width: '140px',
              },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell className='props-col'>ID</TableCell>
                <TableCell>
                  <CopyToClipboardWrapper textToCopy={ebsVolume.volumeId}>
                    <Typography>{ebsVolume.volumeId}</Typography>
                  </CopyToClipboardWrapper>
                </TableCell>
              </TableRow>
              <KmsKeyRow
                kmsKeyId={ebsVolume.kmsKeyId}
                isAwsManagedKey={ebsVolume.isAwsManagedKey}
              />
              <TableRow>
                <TableCell className='props-col'>Type</TableCell>
                <TableCell>
                  <Typography>
                    {volumePresets[ebsVolume.volumeType]?.displayName}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>Size</TableCell>
                <TableCell>
                  {fileSizeFormatter(
                    convertToBytes(ebsVolume.sizeGib, 'GiB'),
                    1,
                    false
                  )}
                </TableCell>
              </TableRow>
              {ebsVolume.iops && (
                <TableRow>
                  <TableCell className='props-col'>IOPS</TableCell>
                  <TableCell>
                    <Typography>{ebsVolume.iops}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {ebsVolume.throughputMiBs && (
                <TableRow>
                  <TableCell className='props-col'>Throughput</TableCell>
                  <TableCell>
                    <Typography>{`${ebsVolume.throughputMiBs} MiB/s`}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Stack>
      </Stack>
    );
  }
};

const GcpDiskSection = ({ resource }: { resource: InventoryResource }) => {
  if (resource.resourceProperties?.gcpDisk) {
    const gcpDisk = resource.resourceProperties.gcpDisk;

    return (
      <Stack className='px-[40px] py-[24px]'>
        <Stack direction={'row'} gap={'60px'}>
          <Table
            className='w-1/2'
            sx={{
              td: {
                padding: '4px 0',
                borderBottom: 'none',
                verticalAlign: 'top',
                lineHeight: '24px',
                height: '24px',
              },

              '& .props-col': {
                fontWeight: '400',
                width: '140px',
              },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell className='props-col'>Type</TableCell>
                <TableCell>
                  <Typography>
                    {diskPresets[gcpDisk.diskType]?.displayName}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Stack>
      </Stack>
    );
  }
};
